import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import styles from './SbRespFollowSearchTopButton.scss';
import { trackFollowSearchButton } from '../../utils/tracking/searchBrowse/trackFollowSearch';
import { locations } from '../../utils/tracking/shared/followSearchTracking';
import { getQueryParam } from '../../utils/uriUtils';

// Components
import { FollowSearchButton } from '../../components/global/FollowSearchButton/FollowSearchButton';
import { TooltipLoader } from '../../global/TooltipLoader';
import { FormattedMessage } from 'react-intl';

class SbRespFollowSearchTopButtonComponent extends React.Component {
    constructor() {
        super();

        this.handleFollowSearchCompleted = this.handleFollowSearchCompleted.bind(this);
        this.shouldAutoFollow = this.shouldAutoFollow.bind(this);

        this.state = {
            showSuccessTooltip: false,
            loadSuccessTooltip: false,
            showHoverTooltip: false,
            loadHoverTooltip: false,
            // Putting this in componentDidMount caused the FollowSearchButton to update before we had user information.
            // We need user information when FollowSearchButton updates to determine appropriate action
            shouldAutoFollow: this.shouldAutoFollow(),
        };
    }

    handleFollowSearchCompleted(isFollowing) {
        const { refetchFollow } = this.props;
        refetchFollow(() => {
            if (isFollowing) {
                // show success tooltip is user follows a search (but not if they unfollow)
                this.setState(
                    {
                        loadSuccessTooltip: true,
                        showSuccessTooltip: true,
                        showHoverTooltip: false,
                    },
                    () => {
                        // close success tooltip after 3 seconds
                        const timer = setTimeout(() => {
                            this.setState({ showSuccessTooltip: false });
                            clearTimeout(timer);
                        }, 3000);
                    }
                );
            }
        });

        trackFollowSearchButton(isFollowing);
    }

    shouldAutoFollow() {
        if (typeof document !== 'undefined') {
            return getQueryParam(document.location.search, 'follow') === 'search';
        }
        return false;
    }
    render() {
        const {
            user,
            uriRef,
            showFollowButton,
            followSearchValue,
            isFollowing,
            isCategorySearch,
        } = this.props;
        const {
            showHoverTooltip,
            loadHoverTooltip,
            loadSuccessTooltip,
            showSuccessTooltip,
            shouldAutoFollow,
        } = this.state;

        // only show hover tooltip if user is not following search
        const mouseProps = {
            onMouseEnter: () =>
                this.setState({ showHoverTooltip: !isFollowing, loadHoverTooltip: !isFollowing }),
            onMouseLeave: () => this.setState({ showHoverTooltip: false }),
        };

        if (!showFollowButton) {
            return null;
        }

        const followSearchCopy = isCategorySearch ? followSearchValue : `"${followSearchValue}"`;

        return (
            <div {...mouseProps} className={styles.container}>
                <FollowSearchButton
                    size="medium"
                    userId={get(user, 'serviceId') || null}
                    user={user}
                    page={uriRef}
                    name={followSearchValue}
                    autoFollow={shouldAutoFollow}
                    favoritesConnection={get(this.props, 'user.favorites') || null}
                    location={locations.SEARCH_BROWSE}
                    onFollowSearchCompleted={this.handleFollowSearchCompleted}
                />
                <TooltipLoader
                    load={loadHoverTooltip}
                    isVisible={showHoverTooltip}
                    closeOnOutsideClick
                    direction="bottom"
                >
                    <FormattedMessage
                        id="sb.SbRespFollowSearchTopButton.hoverText"
                        defaultMessage="Get weekly updates with new arrivals for this category."
                    />
                </TooltipLoader>
                <TooltipLoader
                    load={loadSuccessTooltip}
                    isVisible={showSuccessTooltip}
                    closeOnOutsideClick
                    direction="right"
                >
                    <FormattedMessage
                        id="sb.SbRespFollowSearchTopButton.successText"
                        defaultMessage="Check your email for weekly updates in {name}."
                        values={{
                            name: (
                                <span data-tn="follow-search-value" className={styles.searchTerm}>
                                    {followSearchCopy}
                                </span>
                            ),
                        }}
                    />
                </TooltipLoader>
            </div>
        );
    }
}

SbRespFollowSearchTopButtonComponent.defaultProps = {
    onFollowSearchSuccess: () => {},
};

SbRespFollowSearchTopButtonComponent.propTypes = {
    user: PropTypes.object,
    uriRef: PropTypes.string,
    showFollowButton: PropTypes.bool.isRequired,
    followSearchValue: PropTypes.string.isRequired,
    isFollowing: PropTypes.bool.isRequired,
    onFollowSearchSuccess: PropTypes.func,
    refetchFollow: PropTypes.func,
    isCategorySearch: PropTypes.bool.isRequired,
};

export const SbRespFollowSearchTopButton = createFragmentContainer(
    SbRespFollowSearchTopButtonComponent,
    {
        user: graphql`
            fragment SbRespFollowSearchTopButton_user on User {
                serviceId
                favorites(first: 1, types: $followSearchTypes, pages: $followSearchPages) {
                    ...FollowSearchButton_favoritesConnection
                }
            }
        `,
    }
);
